import React, { useState } from 'react';
import styled from 'styled-components';
import { API_ENDPOINTS } from '../../api/config';
import ElevatedButton from '../Layout/ElevatedButton';

const Form = styled.form`
  max-width: 400px;
  margin: 2rem auto;
  padding: 2rem;
  border: 1px solid ${props => props.theme.buttonPrimary};
  border-radius: 8px;
  background: ${props => props.theme.cardBackground};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  color: ${props => props.theme.text};
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1.25rem;
  margin: 0.5rem 0;
  border: 2px solid ${props => props.theme.buttonPrimary};
  border-radius: 4px;
  font-size: 1rem;
  background: ${props => props.theme.inputBackground};
  color: ${props => props.theme.text};
  transition: all 0.3s ease;
  box-sizing: border-box;

  &::placeholder {
    color: ${props => props.theme.textMuted};
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme.buttonPrimaryHover};
    background: ${props => props.theme.inputBackgroundFocus};
    box-shadow: 0 0 8px rgba(88, 101, 242, 0.3);
  }
`;

const TokenInput = styled(Input)`
  text-align: center;
  font-size: 1.5rem;
  letter-spacing: 0.5rem;

  &::placeholder {
    letter-spacing: normal;
  }
`;

const TwoFactorDisableModal = ({ onSuccess, onCancel }) => {
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate token format
    if (token.length !== 6 || !/^\d{6}$/.test(token)) {
      setError('Please enter a valid 6-digit token');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(API_ENDPOINTS.DISABLE_2FA, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ token })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        onSuccess('Two-factor authentication has been disabled successfully.');
      } else {
        setError(data.error || 'Failed to disable 2FA');
      }
    } catch (error) {
      console.error('2FA disable error:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTokenChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
    if (value.length <= 6) {
      setToken(value);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      <h2 style={{ color: 'var(--color-text)', textAlign: 'center', textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)' }}>
        Disable Two-Factor Authentication
      </h2>

      <p style={{ textAlign: 'center', color: '#b9bbbe', marginBottom: '1rem' }}>
        ⚠️ This will make your account less secure. Enter your current 2FA code to confirm.
      </p>

      {error && (
        <div style={{
          padding: '0.75rem',
          marginBottom: '1rem',
          background: '#dc3545',
          color: 'white',
          borderRadius: '4px',
          textAlign: 'center'
        }}>
          {error}
        </div>
      )}

      <TokenInput
        type="text"
        placeholder="000000"
        value={token}
        onChange={handleTokenChange}
        required
        disabled={loading}
        autoFocus
      />

      <ElevatedButton type="submit" style={{ width: '100%', marginTop: '1rem' }} disabled={loading || token.length !== 6}>
        {loading ? 'Disabling...' : 'Disable 2FA'}
      </ElevatedButton>

      <ElevatedButton
        type="button"
        onClick={onCancel}
        style={{
          width: '100%',
          marginTop: '0.5rem',
          background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
          border: '2px solid #ff4757'
        }}
      >
        Cancel
      </ElevatedButton>
    </Form>
  );
};

export default TwoFactorDisableModal;